"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, TriangleAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Clock, Droplets, Eye, FileCheck, Hammer, PhoneCall, Search, ShieldCheck, Volume2, Wrench, Zap } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const detectionMethods = [{
  id: "acoustic",
  title: "Acoustic Ground & Wall Listening",
  iconName: "Volume2",
  badge: "Non-Invasive",
  description: "Sensitive ultra-sonic microphones amplify water pressure vibrations beneath concrete slabs and behind drywall.",
  features: "Pinpoints sub-surface pipeline fractures without drywall removal",
  accuracy: "99.2% Accuracy",
  bestFor: "Slab leaks & hidden wall pipes"
}, {
  id: "thermal",
  title: "Thermal Infrared Imaging",
  iconName: "Zap",
  badge: "High Precision",
  description: "High-resolution radiometric thermal cameras spot subtle temperature gradients caused by escaping hot or cold water.",
  features: "Maps moisture plumes across ceilings, floors, and cavities",
  accuracy: "Instant Thermal Map",
  bestFor: "Hot water lines & underfloor heating"
}, {
  id: "camera",
  title: "High-Definition Video Inspection",
  iconName: "Eye",
  badge: "Direct Visual",
  description: "Fiber-optic micro cameras travel inside supply lines and sewer mains to reveal hairline cracks, root intrusion, and corrosion.",
  features: "Full digital playback footage with precise depth telemetry",
  accuracy: "Direct Pipe Visual",
  bestFor: "Main lines, sewer pipes & drains"
}, {
  id: "pressure",
  title: "Hydrostatic Pressure Testing",
  iconName: "Activity",
  badge: "System-Wide",
  description: "Precision digital pressure gauges isolate plumbing sections to determine the exact rate and volume of water loss.",
  features: "Validates system structural integrity under closed test conditions",
  accuracy: "PSI Drop Detection",
  bestFor: "Whole-home supply evaluation"
}];
const repairSteps = [{
  stepNumber: "01",
  title: "Rapid Dispatch & Thermal Assessment",
  description: "Our licensed Jonesboro technician arrives equipped with acoustic and thermal gear to diagnose moisture hotspots.",
  highlight: "Average 45-minute response in Clayton County"
}, {
  stepNumber: "02",
  title: "Targeted Micro-Location",
  description: "We isolate the exact coordinate of the pipe rupture to avoid destructive digging or broad drywall demolition.",
  highlight: "Zero unnecessary drywall destruction"
}, {
  stepNumber: "03",
  title: "Precision Pipe Repair",
  description: "We apply targeted sectional pipe replacement, epoxy relining, or copper bypass solutions tailored to the pipe material.",
  highlight: "Code-compliant materials & experienced plumbers"
}, {
  stepNumber: "04",
  title: "Pressure Verification & Warranty",
  description: "The line is repressurized and tested against leaks, followed by written documentation and a full workmanship guarantee.",
  highlight: "100% leak-free verification certification"
}];
const commonWarningSigns = [{
  title: "Unexplained Water Bill Spikes",
  detail: "A sudden rise in your monthly water utility bill often indicates a silent underground supply line leak."
}, {
  title: "Warm Spots on Hardwood or Tile",
  detail: "Warm floor patches usually indicate a ruptured hot water copper line running under your concrete foundation."
}, {
  title: "Musty Odors & Damp Baseboards",
  detail: "Persistent mildew smell or warped trim suggests steady moisture buildup behind interior drywall."
}, {
  title: "Spinning Water Meter Indicator",
  detail: "If the leak indicator dial moves while all interior fixtures are closed, water is continuously escaping."
}];
export default function LeakDetection() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("acoustic");
  return <section id="leakdetectionprocess" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-semibold text-xs tracking-wide uppercase">
            Accurate Diagnostic Technology
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Comprehensive Leak Detection & Rapid Pipe Repair
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Jonesboro Emergency Plumber uses non-invasive acoustic, thermal, and video tools to pinpoint hidden residential and commercial leaks without tearing apart your property.
          </p>
        </div>

        {/* Warning Signs Banner */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mb-16 p-6 sm:p-8 rounded-2xl bg-card border border-border shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-destructive/10 text-destructive shrink-0">
                <AlertTriangle className="size-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground">
                  Common Signs You Have a Hidden Water Leak
                </h3>
                <p className="text-sm text-muted-foreground">
                  Catching pipeline failure early saves thousands in structural restoration.
                </p>
              </div>
            </div>
            <Button asChild variant="outline" className="self-start lg:self-auto shrink-0">
              <a href="tel:4708479076" className="inline-flex items-center gap-2">
                <PhoneCall className="size-4 text-destructive" />
                <span>{"Call 24/7: (470) 847-9076"}</span>
              </a>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {commonWarningSigns.map((sign, i) => <div key={sign.title} data-index={i} className="p-4 rounded-xl bg-background border border-border flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Droplets className="size-4 text-primary shrink-0" />
                    <h4 className="text-sm font-semibold text-foreground">
                      {sign.title}
                    </h4>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {sign.detail}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Detection Methods Section */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
              Non-Invasive Detection Methods
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              Advanced equipment isolates issues under concrete slabs, underground mains, and interior walls.
            </p>
          </div>

          <Tabs defaultValue="acoustic" value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-8 overflow-x-auto pb-2">
              <TabsList className="h-auto p-1.5 bg-muted/70 rounded-xl gap-1">
                <TabsTrigger value="acoustic" className="px-4 py-2 text-xs sm:text-sm">
                  <Volume2 className="size-4 mr-2" />
                  Acoustic Listening
                </TabsTrigger>
                <TabsTrigger value="thermal" className="px-4 py-2 text-xs sm:text-sm">
                  <Zap className="size-4 mr-2" />
                  Thermal Infrared
                </TabsTrigger>
                <TabsTrigger value="camera" className="px-4 py-2 text-xs sm:text-sm">
                  <Eye className="size-4 mr-2" />
                  Video Pipe Camera
                </TabsTrigger>
                <TabsTrigger value="pressure" className="px-4 py-2 text-xs sm:text-sm">
                  <Activity className="size-4 mr-2" />
                  Pressure Testing
                </TabsTrigger>
              </TabsList>
            </div>

            {detectionMethods.map((method, i) => <TabsContent key={method.id} value={method.id} data-index={i} className="focus:outline-none">
                <Card className="border border-border shadow-md bg-card overflow-hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 sm:p-8 items-center">
                    <div className="lg:col-span-7 flex flex-col justify-between h-full">
                      <div>
                        <div className="flex items-center gap-3 mb-4">
                          <Badge variant="secondary" className="font-semibold">
                            {method.badge}
                          </Badge>
                          <span className="text-xs font-semibold text-primary flex items-center gap-1">
                            <CheckCircle2 className="size-3.5" />
                            {method.accuracy}
                          </span>
                        </div>
                        <h4 className="text-2xl font-bold text-foreground mb-3">
                          {method.title}
                        </h4>
                        <p className="text-base text-muted-foreground mb-6 leading-relaxed">
                          {method.description}
                        </p>
                      </div>

                      <div className="space-y-3 pt-4 border-t border-border">
                        <div className="flex items-center gap-2 text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                          <span className="font-medium">{method.features}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-foreground">
                          <Search className="size-4 text-primary shrink-0" />
                          <span><strong>Recommended for:</strong> {method.bestFor}</span>
                        </div>
                      </div>

                      <div className="mt-8 flex flex-wrap gap-4">
                        <Button asChild className="font-semibold">
                          <Link to="/contact/">
                            Request Diagnostic Inspection
                            <ArrowRight className="size-4 ml-1" />
                          </Link>
                        </Button>
                        <Button asChild variant="outline">
                          <Link to="/services/">
                            View All Services
                          </Link>
                        </Button>
                      </div>
                    </div>

                    <div className="lg:col-span-5">
                      <div className="relative rounded-xl overflow-hidden border border-border bg-muted/40 aspect-4/3 flex items-center justify-center p-6 text-center">
                        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/leak_detection_equipment_inspection.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/leak_detection_equipment_inspection.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/leak_detection_equipment_inspection.webp-wvc-sizes" alt="Professional plumber using acoustic leak detector equipment in residential home" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent flex flex-col justify-end p-6 text-left">
                          <span className="text-xs uppercase tracking-wider text-primary font-bold">
                            Diagnostic Gear Ready
                          </span>
                          <p className="text-sm font-semibold text-foreground mt-1">
                            Certified technicians serving Jonesboro & Clayton County, GA
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </TabsContent>)}
          </Tabs>
        </div>

        {/* 4-Step Repair Process */}
        <div className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="outline" className="mb-3 font-semibold text-xs uppercase tracking-wide">
              Step-By-Step Workflow
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
              Our 4-Stage Leak Repair Process
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              From the initial emergency call to the post-repair pressure check, we work quickly with full transparency.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {repairSteps.map((step, i) => <motion.div key={step.stepNumber} data-index={i} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: shouldReduceMotion ? 0 : i * 0.1
          }} className="relative flex">
                <Card className="flex flex-col justify-between border border-border shadow-xs hover:shadow-md transition-[border-color,box-shadow] bg-card w-full">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-3xl font-extrabold text-primary font-mono tracking-tighter">
                        {step.stepNumber}
                      </span>
                      <div className="p-2 rounded-md bg-secondary text-secondary-foreground">
                        {i === 0 && <Clock className="size-4" />}
                        {i === 1 && <Search className="size-4" />}
                        {i === 2 && <Hammer className="size-4" />}
                        {i === 3 && <FileCheck className="size-4" />}
                      </div>
                    </div>
                    <CardTitle className="text-lg font-bold text-foreground">
                      {step.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {step.description}
                    </CardDescription>
                    <Separator />
                    <div className="flex items-center gap-2 text-xs font-semibold text-primary">
                      <ShieldCheck className="size-3.5 shrink-0" />
                      <span>{step.highlight}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>

        {/* Emergency Callout Box */}
        <div className="rounded-2xl bg-primary text-primary-foreground p-8 sm:p-10 shadow-lg relative overflow-hidden">
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="max-w-2xl text-center md:text-left">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-bold uppercase tracking-wider mb-3">
                <Wrench className="size-3.5" />
                Immediate Action Available
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold font-default tracking-tight">
                Suspect a water leak in your Jonesboro property?
              </h3>
              <p className="mt-2 text-primary-foreground/90 text-sm sm:text-base leading-relaxed">
                Do not wait for structural damage or black mold to develop. Our mobile response team is ready 24/7 with diagnostic instruments.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
              <Button asChild size="lg" variant="secondary" className="font-bold">
                <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="size-4 text-primary" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-primary/20 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground hover:text-primary font-semibold">
                <Link to="/contact/">
                  Book Diagnostic
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}